/**
 * InfoPopover — hover/click popover for an explanatory note.
 *
 * Replaces the native `title` attribute for anything a user is meant to
 * discover. `title` is wrong for that job in two ways that compound:
 *
 *   - It waits roughly a second before appearing, which reads as "nothing is
 *     going to happen".
 *   - So the user clicks — and clicking CANCELS the pending tooltip, so it then
 *     never appears at all. The ⓘ icon has actively misled them.
 *
 * This opens on hover with no delay, and a click PINS it open rather than
 * dismissing it, which is what the icon implies. It matches the popover the
 * report-header ⓘ already uses, so one gesture works everywhere on the page.
 *
 * The behaviour lives in useInfoPopover.js, shared with the two cells whose panel
 * is too rich for heading + body (CallsCell, NotesCell). B18 and B31 in
 * docs/DUPLICATION_AUDIT.md moved the hand-rolled hover-only tips onto it.
 *
 * Content: `heading` + `body`, and/or `sections` ([{ heading?, body }]) for a tip
 * with several headed paragraphs. `marker="none"` with `children` makes the
 * children (a badge, a value) the trigger. `mode="describe"` wraps a control that
 * has its own click (see the hook). `label` names an icon-only trigger for screen
 * readers; it defaults to the heading.
 *
 * Its stylesheet is self-contained rather than reusing `.na-type-popover`,
 * which lives in a page stylesheet (alerts-page.css). Borrowing it would make
 * this component's background depend on which pages happen to be imported.
 *
 * Notes on the two non-obvious implementation choices:
 *
 *   - **Portalled to `document.body`.** A transformed ancestor becomes the
 *     containing block for `position: fixed`, and these live inside report
 *     cards that sit next to elements animating `transform`. Positioning inline
 *     works until the day a parent gains one, and then breaks silently.
 *   - **Only one open at a time**, via a module-level registry (in the hook).
 *     Without it a pinned popover stays on screen while the user hovers the next
 *     row, and two explanations float over each other.
 */

import { Fragment } from 'react';
import { createPortal } from 'react-dom';
import useInfoPopover from './useInfoPopover.js';
import './info-popover.css';

const MARKERS = {
    info: { className: 'fe fe-info ip-marker--info', label: 'More information' },
    star: { className: 'ip-marker--star', label: 'More information', text: '*' },
    none: null,
};

const InfoPopover = ({
    children,
    body,
    sections,
    heading = '',
    marker = 'info',
    maxWidth = 300,
    flipBelow,
    mode = 'pin',
    className = '',
    triggerClassName = '',
    label,
}) => {
    const sectionList = Array.isArray(sections) ? sections.filter((sec) => sec && sec.body) : [];
    const hasContent = Boolean(body) || sectionList.length > 0;
    const { open, style, panelId, triggerProps } = useInfoPopover({
        maxWidth, flipBelow, mode, enabled: hasContent,
    });

    const mk = MARKERS[marker] || null;
    // A wrapper around another control is not itself a help affordance.
    const triggerClass = mode === 'describe'
        ? triggerClassName
        : `ip-trigger ${triggerClassName}`.trim();
    /* An icon-only trigger needs a name (the marker is aria-hidden). With children
       the children name it; a wrapper in describe mode is named by its control. */
    const ariaLabel = mode === 'describe'
        ? undefined
        : label || (children ? undefined : heading || mk?.label);

    return (
        <span className={triggerClass || undefined} aria-label={ariaLabel} {...triggerProps}>
            {children}
            {mk ? (
                <i className={mk.className} aria-hidden="true">{mk.text || ''}</i>
            ) : null}
            {open && style
                ? createPortal(
                    <span id={panelId} role="tooltip" className={`ip-popover ${className}`.trim()} style={style}>
                        {heading ? <span className="ip-popover__heading">{heading}</span> : null}
                        {body ? <span className="ip-popover__body">{body}</span> : null}
                        {sectionList.map((sec, i) => (
                            <Fragment key={i}>
                                {sec.heading ? (
                                    <span className={`ip-popover__heading${i > 0 || heading || body ? ' ip-popover__heading--section' : ''}`}>
                                        {sec.heading}
                                    </span>
                                ) : null}
                                <span className="ip-popover__body">{sec.body}</span>
                            </Fragment>
                        ))}
                    </span>,
                    document.body,
                )
                : null}
        </span>
    );
};

export default InfoPopover;
